import { useMemo, useState } from "react";
import { CategoryRankBarChart, StyledPieChart } from "../ChartComponents";

// KPI tile in the updated dashboard style (light bordered card, navy value).
function MetricTile({ label, value, sub }) {
  return (
    <div className="min-w-0 rounded-2xl border border-[#ECEEF4] bg-white px-5 py-4 flex flex-col gap-1.5">
      <span className="text-sm font-medium text-[#4B5266]">{label}</span>
      <span className="truncate text-2xl sm:text-3xl font-extrabold leading-tight tracking-tight text-[#14122B]" title={typeof value === "string" ? value : undefined}>
        {value}
      </span>
      {sub && <span className="text-xs text-[#9AA1B2]">{sub}</span>}
    </div>
  );
}

// Validated colorblind-safe 6-slot categorical set (see dataviz palette
// validation) — only used for the pie's top 6 individually-colored wedges.
// Past 6, hue separation stops being reliable, so the rest fold into gray.
const PIE_COLORS = ["#2a78d6", "#eb6834", "#1baf7a", "#eda100", "#e87ba4", "#008300"];
const OTHER_COLOR = "#B7BFD4";

function ViewToggle({ view, onChange }) {
  const options = [
    { key: "bar", label: "Bar" },
    { key: "pie", label: "Pie" },
    { key: "table", label: "Table" },
  ];
  return (
    <div className="flex gap-1 bg-[#F5F2FF] rounded-xl p-1 shrink-0">
      {options.map((opt) => (
        <button
          key={opt.key}
          type="button"
          onClick={() => onChange(opt.key)}
          className={`text-xs font-semibold rounded-lg px-3 py-1.5 transition ${
            view === opt.key ? "bg-[#14122B] text-white" : "text-[#4B5266] hover:bg-white/70"
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

export default function QueryCategories({ categoryAnalytics, loading, source, onSourceChange }) {
  const [view, setView] = useState("bar");

  const categories = useMemo(() => {
    const raw = categoryAnalytics?.categories || [];
    return raw
      .map((c) => ({ key: c.category, name: c.name, count: c.count }))
      .sort((a, b) => b.count - a.count);
  }, [categoryAnalytics]);

  const pieData = useMemo(() => {
    const top6 = categories.slice(0, 6).map((c, i) => ({ ...c, fill: PIE_COLORS[i] }));
    const rest = categories.slice(6);
    const otherCount = rest.reduce((sum, c) => sum + c.count, 0);
    if (otherCount > 0) {
      top6.push({
        key: "__other__",
        name: `Other (${rest.length} ${rest.length === 1 ? "category" : "categories"})`,
        count: otherCount,
        fill: OTHER_COLOR,
      });
    }
    return top6;
  }, [categories]);

  const sourceGroups = useMemo(() => {
    const groups = { agent: [], assistant: [], direct: false };
    (categoryAnalytics?.sources || []).forEach((opt) => {
      if (opt.group === "agent") groups.agent.push(opt);
      else if (opt.group === "assistant") groups.assistant.push(opt);
      else if (opt.group === "direct") groups.direct = true;
    });
    return groups;
  }, [categoryAnalytics]);

  if (loading) {
    return (
      <div className="rounded-2xl border border-[#ECEEF4] bg-white p-6">
        <div className="flex h-[280px] items-center justify-center text-sm text-slate-400">
          Loading category breakdown…
        </div>
      </div>
    );
  }

  const classifiedCount = categoryAnalytics?.classified_count ?? 0;
  const totalResponses = categoryAnalytics?.total_responses ?? 0;
  const topCategory = categories[0];
  const topPct =
    topCategory && classifiedCount ? Math.round((topCategory.count / classifiedCount) * 1000) / 10 : 0;

  return (
    <div className="w-full min-w-0 rounded-2xl border border-[#ECEEF4] bg-white p-5 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <p className="text-sm text-[#9AA1B2]">
          Interaction type breakdown, classified automatically after each response
        </p>
        <label className="flex w-full sm:w-auto items-center gap-2 text-sm text-[#4B5266]">
          Source
          <select
            className="min-w-0 flex-1 sm:flex-none h-10 text-sm font-medium text-[#14122B] bg-white border border-[#ECEEF4] rounded-xl pl-3 pr-2 cursor-pointer transition-colors hover:border-[#D9DCE6] focus:outline-none focus:border-[#8B5CF6]/50 focus:ring-2 focus:ring-[#8B5CF6]/10"
            value={source || "all"}
            onChange={(e) => onSourceChange(e.target.value === "all" ? null : e.target.value)}
          >
            <option value="all">All sources (global)</option>
            {sourceGroups.agent.length > 0 && (
              <optgroup label="Agents">
                {sourceGroups.agent.map((opt) => (
                  <option key={opt.key} value={opt.key}>{opt.label}</option>
                ))}
              </optgroup>
            )}
            {sourceGroups.assistant.length > 0 && (
              <optgroup label="Assistants">
                {sourceGroups.assistant.map((opt) => (
                  <option key={opt.key} value={opt.key}>{opt.label}</option>
                ))}
              </optgroup>
            )}
            {sourceGroups.direct && <option value="direct">Direct chat (no agent/assistant)</option>}
          </select>
        </label>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 mb-6">
        <MetricTile
          label="Classified interactions"
          value={classifiedCount.toLocaleString()}
          sub={`of ${totalResponses.toLocaleString()} total responses`}
        />
        <MetricTile
          label="Top category"
          value={topCategory ? topCategory.name : "—"}
          sub={topCategory ? `${topCategory.count.toLocaleString()} · ${topPct}% of classified` : undefined}
        />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="min-w-0">
          <h3 className="text-base font-bold tracking-tight text-[#14122B]">Interactions by category</h3>
          <p className="text-xs text-[#9AA1B2] mt-0.5">Sorted by volume · hover a bar for the exact share</p>
        </div>
        <ViewToggle view={view} onChange={setView} />
      </div>

      {categories.length === 0 ? (
        <div className="flex h-[200px] items-center justify-center text-sm text-slate-400">
          No classified interactions in this range yet
        </div>
      ) : (
        <>
          {view === "bar" && (
            <CategoryRankBarChart data={categories} dataKey="count" nameKey="name" />
          )}

          {view === "pie" && (
            <div>
              <StyledPieChart
                data={pieData}
                dataKey="count"
                nameKey="name"
                colors={pieData.map((d) => d.fill)}
                variant="pie"
                legendPosition="right"
                outerRadius={130}
                height={300}
              />
              <p className="text-xs text-slate-400 mt-3 leading-relaxed">
                {categories.length > 6 ? (
                  <>
                    Top 6 categories shown individually; the {categories.length - 6} smallest are grouped into{" "}
                    <b>Other</b> here — the table keeps every category separate.
                  </>
                ) : (
                  <>All categories present for this source fit individually — nothing folded into <b>Other</b>.</>
                )}
              </p>
            </div>
          )}

          {view === "table" && (
            <div className="table-scroll overflow-x-auto rounded-2xl border border-[#ECEEF4]">
              <table className="w-full min-w-[420px] text-sm">
                <thead>
                  <tr className="border-b border-[#ECEEF4] bg-[#FAFAFD]">
                    {["Category", "Count", "Share"].map((h) => (
                      <th key={h} className="py-2.5 pl-5 pr-3 text-left text-[11px] font-semibold uppercase tracking-wide text-[#9AA1B2] sm:pl-6">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {categories.map((c) => {
                    const pct = classifiedCount ? Math.round((c.count / classifiedCount) * 1000) / 10 : 0;
                    return (
                      <tr key={c.key} className="border-b border-[#F1F2F7] last:border-0 hover:bg-gray-50">
                        <td className="py-3 pl-5 pr-3 text-sm font-bold text-[#14122B] sm:pl-6">{c.name}</td>
                        <td className="py-3 pl-5 pr-3 text-sm font-semibold text-[#14122B] tabular-nums sm:pl-6">{c.count.toLocaleString()}</td>
                        <td className="py-3 pl-5 pr-3 text-sm text-[#4B5266] tabular-nums sm:pl-6">{pct}%</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      <div className="mt-4 pt-3 border-t border-[#ECEEF4] flex items-center gap-1.5 text-xs text-[#9AA1B2]">
        Category is assigned by a background classifier once a response finishes — it can take a few minutes to
        appear for the most recent messages.
      </div>
    </div>
  );
}
